import { makeAutoObservable, runInAction } from 'mobx'
// evertel
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'

// GCWSCSR-915: Seat status for department member invites.
// A "seat" is a verified member with a role above guest. Pending (valid,
// unconsumed, non-persistent) DEPARTMENT_INVITEs with a seat-consuming role
// will each take a seat when accepted, so they count toward overcommitment.

export interface DepartmentSeatCapacities {
    verifiedMemberCapacity: number
    verifiedMembersCount: number
    verifiedMembersAvailable: number
}

const SEAT_CONSUMING_ROLES = ['employee', 'management', 'executive']

class DepartmentSeatStatusController {
    departmentId = 0
    capacities: DepartmentSeatCapacities | undefined = undefined
    pendingSeatInviteCount = 0

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init = async (departmentId: number) => {
        if (!departmentId) return

        this.departmentId = departmentId

        await this.refresh()
    }

    refresh = async () => {
        if (!this.departmentId) return

        try {
            const [capacities, pendingInvites] = await Promise.all([
                this.api.Routes.Department.getAvailableCapacities(this.departmentId),
                this.api.Routes.Department.getInvitesCount(this.departmentId, {
                    status: 'valid',
                    type: 'DEPARTMENT_INVITE',
                    isPersistent: false,
                    setConsumerRole: { inq: SEAT_CONSUMING_ROLES },
                    expiresDate: { gt: Date.now() }
                })
            ])

            runInAction(() => {
                this.capacities = capacities as DepartmentSeatCapacities
                this.pendingSeatInviteCount = (pendingInvites as { count?: number })?.count || 0
            })
        } catch (error) {
            // seat status is advisory — don't block the invite UI on a failure
        }
    }

    get seatsAvailable(): number | undefined {
        return this.capacities?.verifiedMembersAvailable
    }

    // No seats left — member invites can't be created/accepted
    get isFull(): boolean {
        return !!this.capacities && this.capacities.verifiedMembersAvailable <= 0
    }

    // Would (seats used + pending seat-consuming invites + new invites) exceed the seat limit?
    willExceedSeats = (newInviteCount: number): boolean => {
        if (!this.capacities) return false
        return (this.capacities.verifiedMembersCount + this.pendingSeatInviteCount + newInviteCount) > this.capacities.verifiedMemberCapacity
    }
}

decorate(injectable(), DepartmentSeatStatusController)
decorate(inject(Api), DepartmentSeatStatusController, 0)

export { DepartmentSeatStatusController }
